import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { Reveal } from "../components/site/Reveal";

export default function About() {
  const { t } = useLang();

  return (
    <div data-testid="about-page">
      <section className="pt-16 pb-12 md:pt-20 md:pb-16">
        <div className="container-art">
          <Reveal>
            <div className="overline mb-6">{t.about_page.overline}</div>
            <h1 className="font-heading text-5xl md:text-7xl font-light tracking-tighter leading-[1.02] max-w-4xl text-balance">
              {t.about_page.title}
            </h1>
            <p className="mt-8 max-w-xl text-base font-light text-foreground/70 leading-relaxed">
              {t.about_page.lead}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28 border-t border-border">
        <div className="container-art grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <Reveal className="md:col-span-5">
            <div className="aspect-[3/4] overflow-hidden bg-sand">
              <img
                src="https://gonzaga.pl/img/pomaranczowa-sylwetka.png"
                alt="Piotr Gonzaga Myszkowski"
                className="w-full h-full object-contain object-bottom"
              />
            </div>
          </Reveal>

          <div className="md:col-span-7 md:pt-12">
            <Reveal>
              <div className="overline mb-5">{t.about_page.bio_h}</div>
              <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-[1.1] text-balance mb-10">
                {t.about.title}
              </h2>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="font-body text-lg font-light leading-relaxed text-foreground/85 mb-6">
                {t.about.paragraph1}
              </p>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="font-body text-lg font-light leading-relaxed text-foreground/85">
                {t.about.paragraph2}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-sand">
        <div className="container-art">
          <Reveal>
            <div className="overline mb-5">{t.about_page.values_h}</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight max-w-2xl mb-16">
              {t.about_page.values_h}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
            {t.about_page.values.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.06}>
                <div className="pt-6 border-t border-ink/15">
                  <h3 className="font-heading text-3xl font-light tracking-tight">{v.t}</h3>
                  <p className="mt-3 max-w-md text-base font-light text-foreground/75 leading-relaxed">
                    {v.d}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="py-24 md:py-32 border-t border-border" data-testid="team-section">
        <div className="container-art">
          <Reveal>
            <div className="overline mb-5">{t.team.overline}</div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight max-w-3xl text-balance mb-6">
              {t.team.title}
            </h2>
            <p className="max-w-xl text-base font-light text-foreground/75 leading-relaxed mb-16">
              {t.team.lead}
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-12">
            {t.team.members.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.08}>
                <article
                  data-testid={`team-member-${i + 1}`}
                  className="pt-6 border-t border-ink/20"
                >
                  <div className="overline mb-4">0{i + 1}</div>
                  <h3 className="font-heading text-3xl font-light tracking-tight">
                    {m.name}
                  </h3>
                  <p className="mt-2 italic font-body text-base font-light text-foreground/70">
                    {m.role}
                  </p>
                  <p className="mt-5 text-base font-light text-foreground/80 leading-relaxed">
                    {m.bio}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <p className="mt-16 max-w-2xl text-base font-light italic text-foreground/65 leading-relaxed border-t border-border pt-8">
              {t.team.others}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 border-t border-border">
        <div className="container-art">
          <Reveal>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-8">
              {t.about.facts.map((f) => (
                <div key={f.k}>
                  <dd className="font-heading text-4xl md:text-5xl font-light tracking-tight">
                    {f.v}
                  </dd>
                  <dt className="mt-3 overline">{f.k}</dt>
                </div>
              ))}
            </dl>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-16">
              <Link
                to="/kontakt"
                data-testid="about-contact-cta"
                className="inline-flex items-center gap-3 bg-ink text-bone px-7 py-3.5 text-[12px] uppercase tracking-[0.24em] font-medium hover:bg-ink/85 transition-colors"
              >
                {t.common.talkAboutArt}
                <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
